import { WidgetConfigurationCard } from '@framework/ui/components/configuration-card/widget/WidgetConfigurationCard.tsx';
import { sortBy } from 'lodash';
import { Fragment } from 'react';
import { type IWidgetConfigurationSlot } from './IWidgetConfigurationSlot.ts';
import {
  type IWidgetConfigurationSlotEntry,
  useWidgetConfigurationSlotEntries,
} from './WidgetConfigurationSlotContext.tsx';

interface ISlotCardGroup {
  slot: IWidgetConfigurationSlot;
  entries: IWidgetConfigurationSlotEntry[];
}

/**
 * Guardia di sviluppo: la stessa `key` registrata con definizioni di slot
 * divergenti produce una card sola con i metadati della prima registrazione,
 * quindi una divergenza è quasi certamente un errore di configurazione.
 * `icon` è escluso dal confronto (ReactNode, identità non significativa).
 */
function errorOnDivergentSlot(
  first: IWidgetConfigurationSlot,
  other: IWidgetConfigurationSlot,
) {
  if (first === other) return;
  if (
    first.title === other.title &&
    first.description === other.description &&
    first.order === other.order
  ) {
    return;
  }
  console.error(
    `Slot di configurazione widget "${first.key}" registrato con definizioni divergenti; vince la prima registrazione.`,
    first,
    other,
  );
}

function groupEntriesBySlot(
  entries: IWidgetConfigurationSlotEntry[],
): ISlotCardGroup[] {
  const groups = new Map<string, ISlotCardGroup>();
  for (const entry of entries) {
    const group = groups.get(entry.slot.key);
    if (!group) {
      groups.set(entry.slot.key, { slot: entry.slot, entries: [entry] });
      continue;
    }
    errorOnDivergentSlot(group.slot, entry.slot);
    group.entries.push(entry);
  }
  return [...groups.values()];
}

/**
 * Renderizza le card del Drawer di configurazione widget a partire dai fill
 * registrati nel `WidgetConfigurationSlotProvider`: una `WidgetConfigurationCard`
 * per ogni slot con almeno un fill, ordinata per `slot.order`; dentro la card i
 * contenuti sono ordinati per `order` del fill (senza `order` in coda, in
 * ordine di scoperta — `sortBy` di lodash è stabile e mette `undefined` in fondo).
 */
export function WidgetConfigurationSlotCards() {
  const entries = useWidgetConfigurationSlotEntries();
  const groups = sortBy(groupEntriesBySlot(entries), group => group.slot.order);

  return (
    <>
      {groups.map(group => (
        <WidgetConfigurationCard
          key={group.slot.key}
          title={group.slot.title}
          icon={group.slot.icon}
          description={group.slot.description}
        >
          {sortBy(group.entries, 'order').map(entry => (
            <Fragment key={entry.key}>{entry.node}</Fragment>
          ))}
        </WidgetConfigurationCard>
      ))}
    </>
  );
}
